import { computed, unref } from 'vue';
import { parseDateAsLocal } from '@/utils/dateUtils';

/**
 * Utility: get a period key string based on a Date and period type
 * - monthly -> 'YYYY-MM'
 * - annual  -> 'YYYY'
 * - quarterly -> 'YYYY-Q{1..4}'
 * - weekly -> 'YYYY-W{01..53}' (ISO week)
 * - daily -> 'YYYY-MM-DD'
 */
function getPeriodKey(dateInput, periodType = 'annual') {
    const parsed = parseDateAsLocal(dateInput);
    if (!parsed) return null;

    const { year, month, day } = parsed;
    const type = String(periodType || 'annual').toLowerCase();

    if (type === 'monthly') {
        const m = String(month).padStart(2, '0');
        return `${year}-${m}`;
    }
    if (type === 'quarterly') {
        const q = Math.floor((month - 1) / 3) + 1; // 1..4
        return `${year}-Q${q}`;
    }
    if (type === 'weekly') {
        // ISO week number - use UTC to avoid timezone issues
        const tmp = new Date(Date.UTC(year, month - 1, day));
        const dayNum = tmp.getUTCDay() || 7;
        tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
        const week = String(weekNo).padStart(2, '0');
        return `${tmp.getUTCFullYear()}-W${week}`;
    }
    if (type === 'daily') {
        const m = String(month).padStart(2, '0');
        const d = String(day).padStart(2, '0');
        return `${year}-${m}-${d}`;
    }

    // annual (default)
    return String(year);
}

/**
 * Utility: format a period key for display.
 */
function formatPeriodLabel(periodKey, periodType = 'annual') {
    const type = String(periodType || 'annual').toLowerCase();

    if (type === 'monthly') {
        const [y, m] = (periodKey || '').split('-').map((x) => parseInt(x, 10));
        const date = new Date(y, (m || 1) - 1, 1);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
        });
    }

    if (type === 'quarterly') {
        return periodKey; // e.g., 2025-Q1
    }

    if (type === 'weekly') {
        return periodKey; // e.g., 2025-W09
    }

    if (type === 'daily') {
        const [y, m, d] = (periodKey || '')
            .split('-')
            .map((x) => parseInt(x, 10));
        const date = new Date(y, (m || 1) - 1, d || 1);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    // annual
    return periodKey;
}

/**
 * Group usage entries by period and compute totals.
 * @param {Array} entries - Array of CardBenefitHistoryUser-like objects
 * @param {String} periodType - 'monthly' | 'annual' | 'quarterly' | 'weekly' | 'daily'
 * @returns {Array<{ periodKey: string, entries: Array, totalAmount: number }>} sorted desc by period
 */
function groupUsageEntriesByPeriod(entries, periodType = 'annual') {
    if (!Array.isArray(entries) || entries.length === 0) return [];

    const buckets = new Map();

    for (const e of entries) {
        const key = getPeriodKey(e.used_at, periodType);

        if (!buckets.has(key)) {
            buckets.set(key, { periodKey: key, entries: [], totalAmount: 0 });
        }
        const bucket = buckets.get(key);
        bucket.entries.push(e);
        const amt = parseFloat(e.total_amount_used ?? 0) || 0;
        bucket.totalAmount += amt;
    }

    // Sort latest period first (lexical works with our key formats)
    const result = Array.from(buckets.values()).sort((a, b) =>
        a.periodKey < b.periodKey ? 1 : a.periodKey > b.periodKey ? -1 : 0
    );

    return result;
}

/**
 * Optional helper: filter entries by user id(s) prior to grouping.
 */
function filterEntriesByUsers(entries, userIds) {
    if (!Array.isArray(entries)) return [];
    if (!userIds || (Array.isArray(userIds) && userIds.length === 0))
        return entries;
    const ids = Array.isArray(userIds) ? new Set(userIds) : new Set([userIds]);
    return entries.filter((e) => ids.has(e.user_id));
}

/**
 * Composable to compute usage groups for a reactive benefit reference.
 * Groups by year with individual entries under each year.
 * @param {Ref|Object} benefitRef - benefit object with `period_type` and `usageRecords`
 * @param {Ref|Number|Array<Number>=} userFilter - optional user id or array of ids
 * @param {Function} calculateDisplayAmountFn - function to calculate display amount for a benefit
 * @param {Function} calculateAnnualValueFn - function to calculate annual value for a benefit
 */
export function useBenefitUsageGroups(
    benefitRef,
    userFilter = null,
    calculateDisplayAmountFn,
    calculateAnnualValueFn
) {
    const groups = computed(() => {
        const benefit = unref(benefitRef);
        if (!benefit) return [];

        // Laravel JSON serialization will expose relations in snake_case (usage_records)
        // while in-memory models may use camelCase (usageRecords).
        // Handle new structure where usage_records might be at the top level
        const relationArray =
            benefit.usage_records ??
            benefit.usageRecords ??
            benefit.benefit?.usage_records ??
            benefit.benefit?.usageRecords;
        const entriesRaw = Array.isArray(relationArray) ? relationArray : [];
        const entries = userFilter
            ? filterEntriesByUsers(entriesRaw, unref(userFilter))
            : entriesRaw;

        // Always group by year, but keep individual entries with dates
        const yearBuckets = new Map();

        // Use provided calculation functions which handle all complex cases
        const displayAmount = calculateDisplayAmountFn
            ? calculateDisplayAmountFn(benefit)
            : 0;

        // Determine if this is a count-only benefit (count-based with no monetary value, or NCRED)
        const currentVersion =
            benefit.benefit ?? benefit.currentVersion ?? benefit;
        const isCountBased =
            currentVersion?.values_summary?.is_count_based ?? false;
        const hasMonetaryValue =
            parseFloat(currentVersion?.amount ?? benefit.amount ?? 0) > 0;
        const currency = currentVersion?.values_summary?.currency;
        const isCountOnly = currency === 'NCRED' || (isCountBased && !hasMonetaryValue);

        for (const entry of entries) {
            // Parse date string as local date to avoid timezone shifts
            const parsed = parseDateAsLocal(entry.used_at);
            if (!parsed) continue;

            const { year: yearNum, date: usedAt } = parsed;
            const year = String(yearNum);

            if (!yearBuckets.has(year)) {
                const yearAnnualValue = calculateAnnualValueFn
                    ? calculateAnnualValueFn(benefit, year)
                    : displayAmount;
                yearBuckets.set(year, {
                    periodKey: year,
                    entries: [],
                    totalAmount: 0,
                    annualValue: yearAnnualValue,
                });
            }

            const bucket = yearBuckets.get(year);
            bucket.entries.push({
                ...entry,
                formattedDate: usedAt.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }),
            });

            // For count-only benefits, sum usage_count; otherwise sum total_amount_used
            const amount = isCountOnly
                ? parseFloat(entry.usage_count ?? 0) || 0
                : parseFloat(entry.total_amount_used ?? 0) || 0;
            bucket.totalAmount += amount;
        }

        // Sort entries within each year by date (most recent first)
        // Use string comparison since dates are in YYYY-MM-DD format
        for (const bucket of yearBuckets.values()) {
            bucket.entries.sort((a, b) => {
                const dateA = String(a.used_at).split('T')[0];
                const dateB = String(b.used_at).split('T')[0];
                return dateB.localeCompare(dateA);
            });
        }

        // Sort years (most recent first)
        const result = Array.from(yearBuckets.values()).sort(
            (a, b) => parseInt(b.periodKey) - parseInt(a.periodKey)
        );

        return result;
    });

    const labelFor = (periodKey) => {
        return periodKey; // Just return the year
    };

    return { groups, labelFor };
}

export {
    groupUsageEntriesByPeriod,
    formatPeriodLabel,
    filterEntriesByUsers,
    getPeriodKey,
};
